/*#
# b3d-trigger

Invisible proximity zone that fires callbacks and dispatches events when
a target (the active camera or a named mesh) enters or exits a spherical
region. Useful for mission waypoints, area-of-effect zones, and cutscene
triggers.

Set `whenEnter` and `whenExit` callback properties from JavaScript, or listen
for `'enter'` / `'exit'` CustomEvents on the element.

⚠️ There is no `onEnter` / `onExit`. Passed to the element creator, an `on*` prop
is a DOM event listener, so `b3dTrigger({ onEnter })` is a listener for the
`'enter'` event and is called with an Event, not the trigger.

## Demo

```js
import { b3d, b3dTrigger, b3dSphere, b3dLight, b3dSkybox, b3dBiped, b3dGround, emptyInput } from 'tosijs-3d'
import { orbitCam } from 'tosijs-3d/demo-utils'
import { tosi, elements } from 'tosijs'
const { div, span, p } = elements

const { demo } = tosi({ demo: { status: 'walking…', dist: '' } })

// A wandering goal (a glowing marker) with a proximity trigger around it.
let goal = { x: 6, z: 5 }
const marker = b3dSphere({ meshName: 'goal', diameter: 0.7, y: 0.35, x: goal.x, z: goal.z, color: '#ffcc00' })
// Radius comfortably exceeds STOP (below) and the trigger sits low (near the
// biped's root at the feet, y≈0) — otherwise the 0.5m vertical gap eats the
// margin and a biped stopping at ~1.2m horizontal lands just outside.
const trigger = b3dTrigger({ x: goal.x, y: 0.3, z: goal.z, radius: 2, debug: true })

// The NPC: a NON-player biped driven by a tiny "walk to the goal" AI. A biped
// polls whatever is on `.inputProvider` every frame, so an AI is just an
// InputProvider emitting the same ControlInput (forward / turn) a player would.
const walker = b3dBiped({ url: '/omnidude.glb', x: -6, z: -6, initialState: 'idle' })
const STOP = 1.2 // how close counts as "arrived"
walker.inputProvider = {
  poll() {
    const m = walker.mesh
    if (!m) return emptyInput()
    const p = m.getAbsolutePosition()
    const dx = goal.x - p.x
    const dz = goal.z - p.z
    const dist = Math.hypot(dx, dz)
    // Live readout of the trigger's own view of the world (its debugState) — so
    // we can see WHY it does/doesn't fire: what target it's watching, whether that
    // target resolves, and its measured distance vs the AI's.
    const ds = trigger.debugState
    demo.dist.value =
      `AI ${dist.toFixed(1)}m · target=${ds.target} · resolved=${ds.targetResolved}` +
      ` · trigDist=${ds.distance ?? '—'} · inside=${ds.inside}`
    // Turn toward the goal (biped forward is +Z) and walk until we're there.
    const f = m.forward
    let turn = Math.atan2(dx, dz) - Math.atan2(f.x, f.z)
    while (turn > Math.PI) turn -= 2 * Math.PI
    while (turn < -Math.PI) turn += 2 * Math.PI
    return {
      ...emptyInput(),
      forward: dist > STOP ? 1 : 0,
      turn: Math.max(-1, Math.min(1, turn * 2)),
    }
  },
}

// Watch the biped (not the camera): point the trigger at its mesh once loaded.
// Give the root a KNOWN name first — a GLB root is often '__root__' or even '',
// which the trigger's getMeshByName/getTransformNodeByName lookup can't resolve.
const wire = setInterval(() => {
  if (walker.mesh) {
    walker.mesh.name = 'walker'
    // Set BOTH ways: the property, and the attribute (drives the reactive value
    // via attributeChangedCallback) — belt-and-suspenders so `target` reliably
    // becomes 'walker' instead of staying the default 'camera'.
    trigger.target = 'walker'
    trigger.setAttribute('target', 'walker')
    clearInterval(wire)
  }
}, 100)

// On arrival: pause, then teleport the goal (marker + trigger) to a random spot
// on the ground. The NPC notices the trigger it's now outside of and walks to the
// new position. Repeat forever — trigger + simple AI in a loop.
trigger.whenEnter = () => {
  demo.status.value = 'reached it — relocating…'
  setTimeout(() => {
    // Pick a spot well AWAY from the biped — otherwise the goal can land on top
    // of it, so it's already inside the relocated trigger and never exits/re-enters
    // (the loop stalls).
    const p = walker.mesh ? walker.mesh.getAbsolutePosition() : { x: 0, z: 0 }
    do {
      goal = { x: (Math.random() - 0.5) * 16, z: (Math.random() - 0.5) * 16 }
    } while (Math.hypot(goal.x - p.x, goal.z - p.z) < 5)
    marker.x = goal.x; marker.z = goal.z
    trigger.x = goal.x; trigger.z = goal.z
    demo.status.value = 'walking…'
  }, 1000)
}

preview.append(
  b3d(
    {
      sceneCreated(el, BABYLON) {
        const camera = orbitCam(el, {
          alpha: -Math.PI / 2, beta: Math.PI / 3.2, radius: 22,
          target: [0, 0, 0], maxElevationDeg: 70,
        })
        camera.lowerRadiusLimit = 8
        camera.upperRadiusLimit = 60
      },
    },
    b3dLight({ y: 1, intensity: 0.8 }),
    b3dSkybox({ timeOfDay: 12 }),
    b3dGround({ size: 20, color: '#556644' }),
    marker,
    trigger,
    walker,
  ),
  div(
    { style: 'position:absolute; top:8px; left:8px; background:rgba(0,0,0,0.6); color:white; padding:8px 12px; border-radius:6px; font:14px monospace' },
    span({ bindText: demo.status }),
    p({ style: 'opacity:0.7' }, span({ bindText: demo.dist })),
  )
)
```

> Watch it rather than drive it: an NPC walks to the marker, the trigger fires,
> the marker relocates, and it goes again. Nothing here is scripted — the
> trigger is a volume and the walking is a behaviour, and the loop is what
> happens when you put them together.
>
> The overlay is the live state: what the trigger last reported, and how far the
> NPC still has to go.

## Attributes

| Attribute | Default | Description |
|-----------|---------|-------------|
| `x` | `0` | Center X |
| `y` | `0` | Center Y |
| `z` | `0` | Center Z |
| `radius` | `5` | Trigger sphere radius |
| `disabled` | `false` | Disable the trigger (default: active) |
| `target` | `'camera'` | `'camera'` or a mesh name to watch |
| `debug` | `false` | Show wireframe sphere |
| `once` | `false` | Fire `whenEnter` once then deactivate |
*/
/*{ "parent": "interacting", "order": 20 }*/

import { B3dChild } from './b3d-utils.js'
import { handlerOf } from './handler-of.js'
import * as BABYLON from '@babylonjs/core'
import type { B3d } from './tosi-b3d.js'

export class B3dTrigger extends B3dChild {
  static preferredTagName = 'tosi-b3d-trigger'

  static shadowStyleSpec = {
    ':host': {
      display: 'none',
    },
  }

  static initAttributes = {
    x: 0,
    y: 0,
    z: 0,
    radius: 5,
    // NOT `active: true` — tosijs treats an absent boolean attribute as false,
    // ignoring an initAttributes `true` default, so a default-true boolean can
    // never turn on. Use the HTML-conventional `disabled` (absent → false → active).
    disabled: false,
    target: 'camera',
    debug: false,
    once: false,
  }

  declare x: number
  declare y: number
  declare z: number
  declare radius: number
  declare disabled: boolean
  declare target: string
  declare debug: boolean
  declare once: boolean

  owner: B3d | null = null
  /*
  `when*`, because this is a COMPONENT.

  The element creator binds any `on*` prop as a DOM event LISTENER, so
  `b3dTrigger({ onEnter })` did not set this field — it added a listener for the
  `'enter'` CustomEvent below. That fires, so it LOOKED wired, but the callback
  received an Event instead of the trigger, and `trigger.onEnter = fn` (direct
  assignment, which does set the field) handed over a trigger. The same callback
  got two different arguments depending on how you attached it.

  `whenEnter` cannot be read as an event name, which is the same reason
  `whenImpact` and `whenDestroyed` are spelled that way. The old pair was
  removed in 0.10.
  */
  whenEnter: ((trigger: B3dTrigger) => void) | null = null
  whenExit: ((trigger: B3dTrigger) => void) | null = null

  private _inside = false
  private _beforeRender: (() => void) | null = null
  private debugMesh: BABYLON.Mesh | null = null

  content = () => ''

  sceneReady(owner: B3d, _scene: BABYLON.Scene) {
    this.owner = owner

    this._beforeRender = () => this.checkProximity()
    this.owner.scene.registerBeforeRender(this._beforeRender)

    this.updateDebugMesh()
  }

  sceneDispose() {
    if (this.owner && this._beforeRender) {
      this.owner.scene.unregisterBeforeRender(this._beforeRender)
      this._beforeRender = null
    }
    this.disposeDebugMesh()
    this.owner = null
  }

  render() {
    super.render()
    if (!this.owner) return
    this.updateDebugMesh()
  }

  /** Whether the target is currently inside the trigger */
  get inside(): boolean {
    return this._inside
  }

  /**
   * Tuned state for debugging — read `el.debugState` from the console or via
   * `hj eval`. Surfaces exactly why a trigger is (not) firing: whether its target
   * name resolves, the live distance, and the radius it's tested against.
   */
  get debugState() {
    const attrs = this as any
    const tp = this.owner ? this.resolveTargetPosition() : null
    const here = new BABYLON.Vector3(attrs.x, attrs.y, attrs.z)
    return {
      disabled: attrs.disabled,
      target: attrs.target,
      targetResolved: tp != null,
      distance: tp ? +BABYLON.Vector3.Distance(tp, here).toFixed(2) : null,
      radius: attrs.radius,
      inside: this._inside,
      position: [attrs.x, attrs.y, attrs.z],
    }
  }

  private checkProximity() {
    if (this.owner == null) return
    const attrs = this as any
    if (attrs.disabled) return

    const targetPos = this.resolveTargetPosition()
    if (!targetPos) return

    const triggerPos = new BABYLON.Vector3(attrs.x, attrs.y, attrs.z)
    const dist = BABYLON.Vector3.Distance(targetPos, triggerPos)

    if (dist < attrs.radius && !this._inside) {
      this._inside = true
      // The removed `on*` name is reported once if someone still assigns it.
      handlerOf<(t: B3dTrigger) => void>(
        this as unknown as Record<string, unknown>,
        'whenEnter',
        'onEnter',
        '<tosi-b3d-trigger>'
      )?.(this)
      this.dispatchEvent(
        new CustomEvent('enter', { detail: { trigger: this }, bubbles: true })
      )
      if (attrs.once) {
        ;(this as any).disabled = true
      }
    } else if (dist >= attrs.radius && this._inside) {
      this._inside = false
      // The removed `on*` name is reported once if someone still assigns it.
      handlerOf<(t: B3dTrigger) => void>(
        this as unknown as Record<string, unknown>,
        'whenExit',
        'onExit',
        '<tosi-b3d-trigger>'
      )?.(this)
      this.dispatchEvent(
        new CustomEvent('exit', { detail: { trigger: this }, bubbles: true })
      )
    }
  }

  private resolveTargetPosition(): BABYLON.Vector3 | null {
    if (this.owner == null) return null
    const attrs = this as any
    if (attrs.target === 'camera') {
      const cam = this.owner.scene.activeCamera
      return cam ? cam.globalPosition : null
    }
    // Fall back to transform nodes: a GLB/instantiated model's root (what
    // `biped.mesh` points at) is usually a TransformNode named `__root__`, which
    // getMeshByName can't see — so a named biped/vehicle target would never
    // resolve and the trigger would silently never fire.
    const node =
      this.owner.scene.getMeshByName(attrs.target) ??
      this.owner.scene.getTransformNodeByName(attrs.target)
    return node ? node.getAbsolutePosition() : null
  }

  private updateDebugMesh() {
    if (this.owner == null) return
    const attrs = this as any

    if (attrs.debug && !this.debugMesh) {
      this.debugMesh = BABYLON.MeshBuilder.CreateSphere(
        'trigger-debug',
        { diameter: 2, segments: 16 },
        this.owner.scene
      )
      const mat = new BABYLON.StandardMaterial(
        'trigger-debug-mat',
        this.owner.scene
      )
      mat.wireframe = true
      mat.emissiveColor = new BABYLON.Color3(0, 1, 0)
      mat.disableLighting = true
      this.debugMesh.material = mat
      this.debugMesh.isPickable = false
    }

    if (this.debugMesh) {
      this.debugMesh.position.set(attrs.x, attrs.y, attrs.z)
      this.debugMesh.scaling.setAll(attrs.radius)
      this.debugMesh.setEnabled(attrs.debug)
    }

    if (!attrs.debug && this.debugMesh) {
      this.disposeDebugMesh()
    }
  }

  private disposeDebugMesh() {
    if (this.debugMesh) {
      this.debugMesh.dispose()
      this.debugMesh = null
    }
  }
}

export const b3dTrigger = B3dTrigger.elementCreator()
